// Global brand theming. Presets + custom palette, applied at runtime by overriding
// the emerald-* Tailwind utilities (the app's brand color) with the chosen palette.

export const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

export const PRESETS = [
  { id: "emerald", name: "إيميرالد (الافتراضي)", palette: { 50: "#ecfdf5", 100: "#d1fae5", 200: "#a7f3d0", 300: "#6ee7b7", 400: "#34d399", 500: "#10b981", 600: "#059669", 700: "#047857", 800: "#065f46", 900: "#064e3b" } },
  { id: "blue", name: "أزرق مؤسسي", palette: { 50: "#eff6ff", 100: "#dbeafe", 200: "#bfdbfe", 300: "#93c5fd", 400: "#60a5fa", 500: "#3b82f6", 600: "#2563eb", 700: "#1d4ed8", 800: "#1e40af", 900: "#1e3a8a" } },
  { id: "violet", name: "بنفسجي ملكي", palette: { 50: "#f5f3ff", 100: "#ede9fe", 200: "#ddd6fe", 300: "#c4b5fd", 400: "#a78bfa", 500: "#8b5cf6", 600: "#7c3aed", 700: "#6d28d9", 800: "#5b21b6", 900: "#4c1d95" } },
  { id: "amber", name: "رملي فاخر", palette: { 50: "#fffbeb", 100: "#fef3c7", 200: "#fde68a", 300: "#fcd34d", 400: "#fbbf24", 500: "#f59e0b", 600: "#d97706", 700: "#b45309", 800: "#92400e", 900: "#78350f" } },
  { id: "rose", name: "وردي / فوشيا", palette: { 50: "#fff1f2", 100: "#ffe4e6", 200: "#fecdd3", 300: "#fda4af", 400: "#fb7185", 500: "#f43f5e", 600: "#e11d48", 700: "#be123c", 800: "#9f1239", 900: "#881337" } },
  { id: "teal", name: "فيروزي", palette: { 50: "#f0fdfa", 100: "#ccfbf1", 200: "#99f6e4", 300: "#5eead4", 400: "#2dd4bf", 500: "#14b8a6", 600: "#0d9488", 700: "#0f766e", 800: "#115e59", 900: "#134e4b" } },
  { id: "indigo", name: "نيلي", palette: { 50: "#eef2ff", 100: "#e0e7ff", 200: "#c7d2fe", 300: "#a5b4fc", 400: "#818cf8", 500: "#6366f1", 600: "#4f46e5", 700: "#4338ca", 800: "#3730a3", 900: "#312e81" } },
  { id: "slate", name: "رمادي أنيق", palette: { 50: "#f8fafc", 100: "#f1f5f9", 200: "#e2e8f0", 300: "#cbd5e1", 400: "#94a3b8", 500: "#64748b", 600: "#475569", 700: "#334155", 800: "#1e293b", 900: "#0f172a" } },
];

export const DEFAULT_THEME = { id: "emerald", palette: PRESETS[0].palette };

const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
};
const rgbStr = (hex) => { const { r, g, b } = hexToRgb(hex); return `${r} ${g} ${b}`; };

const rgbToHsl = ({ r, g, b }) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
};
const hslToHex = (h, s, l) => {
  s /= 100; l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const to = (x) => clamp(x * 255).toString(16).padStart(2, "0");
  return `#${to(f(0))}${to(f(8))}${to(f(4))}`;
};

// Build a full 50..900 palette from a single base hex (used at ~600).
export const derivePalette = (baseHex) => {
  const hsl = rgbToHsl(hexToRgb(baseHex));
  const LMAP = { 50: 97, 100: 94, 200: 86, 300: 77, 400: 66, 500: 57, 600: 47, 700: 39, 800: 31, 900: 24 };
  const SMAP = { 50: Math.min(hsl.s, 85), 100: Math.min(hsl.s, 88) };
  const out = {};
  for (const sh of SHADES) out[sh] = hslToHex(hsl.h, SMAP[sh] ?? hsl.s, LMAP[sh]);
  return out;
};

const OPACITIES = [5, 10, 15, 20, 25, 30, 40, 50, 60, 70, 80, 90];
const BASE_PROPS = { bg: "background-color", text: "color", border: "border-color", ring: "--tw-ring-color", fill: "fill", stroke: "stroke", shadow: "--tw-shadow-color", from: "--tw-gradient-from", to: "--tw-gradient-to", via: "--tw-gradient-via", decoration: "text-decoration-color" };
const HOVER_PROPS = { bg: "background-color", text: "color", border: "border-color" };
const OPACITY_PROPS = { bg: "background-color", text: "color", border: "border-color", ring: "--tw-ring-color", shadow: "--tw-shadow-color" };

export const buildThemeCSS = (palette) => {
  let css = "";
  for (const sh of SHADES) {
    const hex = palette[sh]; if (!hex) continue;
    const rgb = rgbStr(hex);
    for (const [pfx, prop] of Object.entries(BASE_PROPS)) {
      css += `.${pfx}-emerald-${sh}{${prop}:${hex}!important}`;
    }
    for (const [pfx, prop] of Object.entries(HOVER_PROPS)) {
      css += `.hover\\:${pfx}-emerald-${sh}:hover{${prop}:${hex}!important}`;
      css += `.focus\\:${pfx}-emerald-${sh}:focus{${prop}:${hex}!important}`;
      css += `.group:hover .group-hover\\:${pfx}-emerald-${sh}{${prop}:${hex}!important}`;
    }
    for (const op of OPACITIES) {
      const val = `rgb(${rgb} / ${op / 100})`;
      for (const [pfx, prop] of Object.entries(OPACITY_PROPS)) {
        css += `.${pfx}-emerald-${sh}\\/${op}{${prop}:${val}!important}`;
        css += `.hover\\:${pfx}-emerald-${sh}\\/${op}:hover{${prop}:${val}!important}`;
      }
    }
  }
  return css;
};

// Apply (or clear) the theme by injecting a global <style> tag.
export const applyTheme = (theme) => {
  const id = "wz-brand-theme";
  let el = document.getElementById(id);
  const palette = theme && theme.palette;
  if (!palette || theme.id === "emerald") { if (el) el.remove(); return; }
  const css = buildThemeCSS(palette);
  if (!el) { el = document.createElement("style"); el.id = id; document.head.appendChild(el); }
  el.textContent = css;
};

export const normalizeTheme = (theme) => {
  if (theme && theme.palette && theme.palette[600]) return { id: theme.id || "custom", palette: theme.palette };
  return DEFAULT_THEME;
};
